import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useSupplierDeals } from "../../api/deals.js";
import { useFinalizeDeal } from "../../api/invoices.js";
import { AppShell } from "../../components/AppShell.js";
import { buttonClass, inputClass } from "../../components/Field.js";
import { formatMoney, formatQty } from "../../lib/format.js";

/** A deal without a SKU is free-text stock — treated as a plain whole-unit count for display. */
const unitOf = (deal: { sku: { unit: string; isDivisible: boolean } | null }) =>
  deal.sku ?? { unit: "개", isDivisible: false };

export function SupplierDealFinalizePage() {
  const { dealId = "" } = useParams();
  const navigate = useNavigate();
  const { data, isPending } = useSupplierDeals();
  const finalize = useFinalizeDeal();
  const deal = data?.find((d) => d.id === dealId);
  // Only claims not yet finalized (fulfilledQty null) are billable here — matches what the
  // finalize endpoint itself requires a line for.
  const pending = deal?.claimants.filter((c) => c.fulfilledQty === null) ?? [];

  // Default is the claimed quantity — the supplier only edits the rows that differed.
  const [actual, setActual] = useState<Record<string, string>>({});
  const valueFor = (claimId: string, qty: string) => actual[claimId] ?? qty;

  if (isPending) {
    return (
      <AppShell title="납품 확정">
        <p className="text-stone-500">불러오는 중…</p>
      </AppShell>
    );
  }

  if (!deal) {
    return (
      <AppShell title="납품 확정">
        <p className="text-red-600">특가 정보를 찾을 수 없습니다.</p>
      </AppShell>
    );
  }

  if (deal.status !== "open") {
    return (
      <AppShell title="납품 확정">
        <p className="text-red-600">진행 중인 특가만 납품 확정할 수 있습니다.</p>
      </AppShell>
    );
  }

  const unit = unitOf(deal);

  return (
    <AppShell title="납품 확정">
      <p className="mb-2 font-semibold">
        {deal.title} · {formatMoney(deal.unitPrice)} / {unit.unit}
      </p>
      <p className="mb-4 text-sm text-stone-600">
        실제로 납품한 수량을 입력해 주세요. 청구서는 이 수량으로 발행됩니다. 공급하지 못한 곳은 0을
        입력하면 청구서가 만들어지지 않습니다.
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          finalize.mutate(
            {
              id: deal.id,
              lines: pending.map((c) => ({
                claimId: c.claimId,
                fulfilledQty: valueFor(c.claimId, c.qty),
              })),
            },
            { onSuccess: () => navigate("/supplier/invoices") },
          );
        }}
      >
        <ul className="mb-4">
          {pending.map((c) => {
            const value = valueFor(c.claimId, c.qty);
            const noDelivery = Number(value) === 0;
            return (
              <li key={c.claimId} className="border-b border-stone-200 py-3">
                <div className="mb-1 flex justify-between">
                  <span className="font-medium">{c.restaurantName}</span>
                  <span className="text-sm text-stone-500">신청 {formatQty(c.qty, unit)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    aria-label={`${c.restaurantName} 실납품 수량`}
                    inputMode="decimal"
                    className={inputClass}
                    value={value}
                    onChange={(e) => setActual({ ...actual, [c.claimId]: e.target.value })}
                  />
                  <span>{unit.unit}</span>
                </div>
                {noDelivery ? (
                  <p className="mt-1 text-sm text-amber-700">0 입력 시 이 식당에는 청구서가 발행되지 않습니다.</p>
                ) : null}
              </li>
            );
          })}
        </ul>

        {pending.length === 0 ? <p className="mb-4 text-stone-500">신청한 식당이 없습니다.</p> : null}

        {finalize.error ? (
          <p role="alert" className="mb-3 text-sm text-red-600">
            {(finalize.error as Error).message}
          </p>
        ) : null}
        <button type="submit" className={buttonClass} disabled={finalize.isPending || pending.length === 0}>
          {finalize.isPending ? "발행 중…" : "납품 확정하고 청구서 발행"}
        </button>
      </form>
    </AppShell>
  );
}
